<!-- ViewSubmission.vue - page displays a single submission -->
<!-- ViewSubmission.vue - one report. Order: summary, status, story, evidence, ideas, ratings, support, context, videos. -->
<template>
  <div class="report view">
    <div class="overlay" v-if="showShareReport" @click="closeOverlay">
      <div class="share-report">
        <button class="close-button" :aria-label="$t('common.actions.close')" @click="toggleShareReport">
          <i class="fas fa-times"></i>
        </button>
        <ShareReport :submission="submission" @close="toggleShareReport" />
      </div>
    </div>

    <header class="report-bar">
      <router-link :to="$lp('/explore')" class="btn-round" :aria-label="$t('common.actions.back')" :title="$t('common.actions.back')">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </router-link>
      <div class="report-bar__id">
        <span class="muted">{{ $t('report.detail.report') }}</span>
        <span class="mono">{{ submission.submission_id }}</span>
      </div>
      <button type="button" class="btn-round" :aria-label="$t('common.actions.share')" :title="$t('common.actions.share')" @click="toggleShareReport">
        <i class="fas fa-share" aria-hidden="true"></i>
      </button>
    </header>

    <section class="summary" aria-labelledby="report-title">
      <ul v-if="submission.imageUrls && submission.imageUrls.length" class="photos">
        <li v-for="(imageUrl, index) in submission.imageUrls" :key="index">
          <button
            type="button"
            class="photo"
            :aria-label="$t('report.detail.openPhoto', { n: index + 1, total: submission.imageUrls.length })"
            @click="selectedImageIndex = index"
          >
            <img :src="imageUrl" :alt="$t('report.detail.photoAlt', { n: index + 1 })" />
          </button>
        </li>
      </ul>
      <div v-if="selectedImageIndex !== null" class="overlay" @click="closeImageOverlay">
        <div class="image-overlay-content" role="dialog" aria-modal="true" :aria-label="$t('report.detail.photoViewer')" @click.stop>
          <div class="image-container">
            <img :src="submission.imageUrls[selectedImageIndex]" :alt="$t('report.detail.photoAlt', { n: selectedImageIndex + 1 })" />
          </div>
          <div class="image-navigation">
            <button type="button" class="arrow left" :aria-label="$t('report.detail.prevPhoto')" @click="previousImage">
              <i class="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button type="button" class="arrow right" :aria-label="$t('report.detail.nextPhoto')" @click="nextImage">
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
            <button type="button" class="arrow close-photo" :aria-label="$t('report.detail.closePhotoViewer')" @click="closeImageOverlay">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="meta-row">
        <SeverityLabel :severity="submission.severity_level" />
        <span v-if="photoCount" class="muted">{{ $t('report.detail.photos', { n: photoCount }, photoCount) }}</span>
      </div>
      <ul v-if="happened.length" class="happened" :aria-label="$t('report.severityScale.whatHappens')">
        <li v-for="k in happened" :key="k">{{ $t('report.severityScale.chips.' + k) }}</li>
      </ul>
      <h1 id="report-title">{{ title }}</h1>
      <p class="place">
        <a v-if="mapUrl" :href="mapUrl" target="_blank" rel="noopener noreferrer" :title="$t('report.detail.openMap')">
          <i class="fas fa-location-dot" aria-hidden="true"></i> {{ place }}
        </a>
        <span v-else>{{ place }}</span>
      </p>
    </section>

    <section v-if="!isPraise" class="card status" aria-labelledby="status-h">
      <div class="status-head">
        <h2 id="status-h">{{ $t('report.detail.status') }}</h2>
        <span v-if="reportedWhen" class="muted">{{ $t('report.detail.reportedAgo', { when: reportedWhen }) }}</span>
      </div>
      <StatusStepper
        :step="statusStep"
        :reported-at="submission.created"
        :status-at="submission.status_at || submission.updated"
        :note="submission.status_note"
        :note-at="submission.status_note_at"
      />
    </section>

    <section v-if="submission.comment" aria-labelledby="story-h">
      <h2 id="story-h">{{ $t('report.detail.whatHappened') }}</h2>
      <p id="story-text" class="story" :class="{ clamped: isLong && !descExpanded }">{{ submission.comment }}</p>
      <button
        v-if="isLong"
        type="button"
        class="link-btn"
        :aria-expanded="descExpanded ? 'true' : 'false'"
        aria-controls="story-text"
        @click="descExpanded = !descExpanded"
      >
        {{ descExpanded ? $t('report.detail.readLess') : $t('report.detail.readMore') }}
      </button>
    </section>

    <section v-if="submission.all_labels && submission.all_labels.length" aria-labelledby="detected-h">
      <h2 id="detected-h">{{ $t('report.detail.detected') }}</h2>
      <p class="muted">{{ $t('report.detail.detectedNote') }}</p>
      <LabelsWrapper :labels="submission.all_labels" :readonly="true" />
    </section>

    <div v-if="hideAi" class="card tonal ai-hidden">
      <p class="muted">{{ $t('report.detail.aiHidden') }}</p>
      <button type="button" class="btn-standard" @click="setHideAi(false)">{{ $t('report.detail.aiShow') }}</button>
    </div>
    <template v-else>
      <AdviceSection
        :h2="$t('report.detail.actionIdeasTitle')"
        :subtitle="$t('report.detail.actionIdeasSubtitle')"
        :result="submission.advice_array"
        :iteration_used="submission.iteration_used"
        :model_used="submission.model_used"
        dismissable
        @update:result="result = $event"
      />
      <button type="button" class="link-btn hide-ai" @click="setHideAi(true)">
        <i class="fas fa-eye-slash" aria-hidden="true"></i> {{ $t('report.detail.aiHide') }}
      </button>
    </template>

    <template v-if="submission.advice_comment">
      <blockquote class="card idea">
        <p class="idea-label">{{ $t('report.detail.communityIdea') }}</p>
        <p v-html="addExternalLinkLabels(submission.advice_comment)"></p>
      </blockquote>

      <section aria-labelledby="rate-h" class="rate">
        <h2 id="rate-h">{{ $t('report.detail.rateTitle') }}</h2>
        <p class="muted">{{ $t('report.detail.rateIntro') }}</p>
        <CategoryRating
          v-for="rating in categoryRatings"
          :key="rating.key"
          :category="rating.category"
          :label="$t('report.detail.criteria.' + rating.key + '.name')"
          :description="$t('report.detail.criteria.' + rating.key + '.desc')"
          :id="submission.id"
        />
      </section>
    </template>

    <section class="card tonal support" aria-labelledby="support-h">
      <h2 id="support-h">{{ $t('report.detail.supportTitle') }}</h2>
      <p class="muted">{{ $t('report.detail.supportIntro') }}</p>
      <div class="support-actions">
        <button type="button" class="btn-cta" :aria-label="$t('report.detail.supportAria', { n: submission.upvotes })" @click="voteSubmission('upvotes')">
          <i class="fas fa-arrow-up" aria-hidden="true"></i> {{ $t('report.detail.support', { n: submission.upvotes }) }}
        </button>
        <button type="button" class="btn-standard" :aria-label="$t('report.detail.disagreeAria', { n: submission.downvotes })" @click="voteSubmission('downvotes')">
          <i class="fas fa-arrow-down" aria-hidden="true"></i> {{ $t('report.detail.disagree', { n: submission.downvotes }) }}
        </button>
      </div>
    </section>

    <CyclingConditions :city="submission.city" :cyclingConditions="submission.cyclingConditions" />

    <AdviceSection
      :h2="$t('report.detail.curatedTitle')"
      :subtitle="$t('report.detail.curatedSubtitle')"
      :result="this.contentRecs.concat(submission.content_array || [])"
      @update:result="result = $event"
    />

    <div class="next">
      <button type="button" class="btn-standard" :disabled="shuffling" :aria-busy="shuffling ? 'true' : 'false'" @click="shuffleToNextSubmission(submission.geo)">
        <i class="fas fa-random" aria-hidden="true"></i>
        {{ $t('report.detail.shuffle') }}
      </button>
      <p v-if="shuffleError" class="muted" role="alert">{{ $t('report.detail.shuffleError') }}</p>
    </div>
  </div>
</template>

<script>
import AdviceSection from "@/components/AdviceSection.vue";
import LabelsWrapper from "@/components/LabelsWrapper.vue";
import CategoryRating from "@/components/CategoryRating.vue";
import SeverityLabel from "@/components/SeverityLabel.vue";
import CyclingConditions from "@/components/CyclingConditions.vue";
import ShareReport from "@/components/ShareReport.vue";
import StatusStepper from "@/components/StatusStepper.vue";
import axios from "axios";

// core version + navigation, pagination modules:

export default {
  name: "ViewSubmission",
  components: {
    AdviceSection,
    LabelsWrapper,
    CategoryRating,
    SeverityLabel,
    CyclingConditions,
    ShareReport,
    StatusStepper,
  },
  setup() {},

  data() {
    return {
      submission: {
        upvotes: 0,
        downvotes: 0,
      },
      showShareReport: false,
      showCyclingConditions: false,
      submissionId: this.$route.params.id,
      shuffling: false,
      shuffleError: false,
      selectedImageIndex: null,
      descExpanded: false,
      hideAi: this.readHideAi(),
      contentRecs: [],
      // category = stable key used for storage; label and description are translated in the template
      categoryRatings: [
        { key: "creativity", category: "Creativity" },
        { key: "effectiveness", category: "Effectiveness" },
        { key: "feasibility", category: "Feasibility" },
      ],
    };
  },
  computed: {
    // The location is the headline: reports have no title of their own.
    title() {
      const parts = (this.submission.address || "").split(",").map((p) => p.trim()).filter(Boolean);
      return parts.slice(0, 2).join(", ") || this.submission.city || "";
    },
    place() {
      return [this.submission.city, this.submission.region, this.submission.country].filter(Boolean).join(", ");
    },
    mapUrl() {
      const geo = this.submission.geo;
      if (!geo) return "";
      const [lat, lon] = geo.split(",");
      return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=14`;
    },
    // Praise and compliments do not need to be fixed, so they have no status
    // What the reporter said happens here (from the report's free-form details)
    happened() {
      const known = ['slow', 'blocked', 'bumpy', 'swerve', 'fell', 'unsafe'];
      const answers = this.submission && this.submission.unstructured_details && this.submission.unstructured_details.rating
        ? this.submission.unstructured_details.rating.answers : [];
      return Array.isArray(answers) ? answers.filter((k) => known.includes(k)) : [];
    },
    isPraise() {
      return Number(this.submission.severity_level) === 1;
    },
    photoCount() {
      return this.submission.imageUrls ? this.submission.imageUrls.length : 0;
    },
    isLong() {
      return (this.submission.comment || "").length > 240;
    },
    // 1 reported, 2 in review, 3 planned, 4 fixed. Reports without a status field are "reported".
    statusStep() {
      return { reported: 1, in_review: 2, planned: 3, fixed: 4 }[this.submission.status] || 1;
    },
    reportedWhen() {
      const created = this.submission.created && new Date(String(this.submission.created).replace(" ", "T"));
      if (!created || isNaN(created)) return "";
      const days = Math.round((created - Date.now()) / 86400000);
      const rtf = new Intl.RelativeTimeFormat(this.$i18n.locale, { numeric: "auto" });
      if (Math.abs(days) >= 30) return rtf.format(Math.round(days / 30), "month");
      if (Math.abs(days) >= 1) return rtf.format(days, "day");
      return rtf.format(Math.round((created - Date.now()) / 3600000), "hour");
    },
    filteredCyclingConditions() {
      // Create a new object where keys starting with 'BIC_' are excluded
      let filtered = Object.keys(this.submission.cyclingConditions).reduce(
        (result, key) => {
          if (!key.startsWith("BIC_")) {
            result[key] = this.submission.cyclingConditions[key];
          }
          return result;
        },
        {},
      );

      return filtered;
    },
  },

  mounted() {
    this.setMetaTags();

    this.fetchSubmissionDetails().catch((error) => {
      console.error(error);
    });
    document.addEventListener("keydown", this.handleKeyDown);

    // wait for submission to be fetched, then update meta tags
    this.$watch(
      () => this.submission,
      () => {
        this.setMetaTags(
          this.submission.city + ", " + this.submission.country,
          this.submission.address,
        );
      },
    );

    // this.fetchAdvice();
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  },

  methods: {
    setMetaTags(title, description) {
      // Set the document title
      document.title = this.$t('meta.view.title');

      // Set or update the meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.name = "description";
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = this.$t('meta.view.description');
      // add country to meta description
      if (title && description) {
        document.title = this.$t('meta.view.title') + " - " + title;
        metaDescription.content =
          this.$t('meta.view.description') + " - " + description;
      }
      // ...
    },

    // The choice to hide AI ideas is a per-person preference kept in this browser
    readHideAi() {
      try { return localStorage.getItem("sr-hide-ai") === "1"; } catch (e) { return false; }
    },
    setHideAi(value) {
      this.hideAi = value;
      try { localStorage.setItem("sr-hide-ai", value ? "1" : "0"); } catch (e) { /* storage unavailable: preference lasts for this visit */ }
    },
    toggleCyclingConditions() {
      this.showCyclingConditions = !this.showCyclingConditions;
    },
    toggleShareReport() {
      this.showShareReport = !this.showShareReport;
    },
    closeOverlay() {
      if (event.target.classList.contains("overlay")) {
        this.showShareReport = false;
      }
    },
    closeImageOverlay(event) {
      if (event.target.classList.contains("overlay")) {
        this.selectedImageIndex = null;
      }
    },
    async fetchSubmissionDetails() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_PB_URL}/api/collections/reports/records?filter=submission_id='${this.submissionId}'`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.submission = data.items[0];
        this.fetchImageDetails(this.submission.id);
        if (this.submission.country === "Germany") {
          // const geo = this.submission.geo.split(',');

          this.submission.cyclingConditions = await this.fetchCyclingConditions(
            this.submission.city,
          );
          // this.submission.cyclingConditions = await this.fetchCyclingConditions(geo);
          if (!this.submission.cyclingConditions) {
            const geo = this.submission.geo.split(",");
            this.submission.cyclingConditions =
              await this.fetchCyclingConditions(geo);
            // make the geo a string - it gets stripped out otherwise
          }
        }
        this.getContentRecs();
      } catch (e) {
        console.error(e);
      }
    },
    async fetchCyclingConditions(location) {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/cycling-conditions/${location}`,
        );
        let cyclingConditionsString = response.data;

        // Replace NaN values with null
        cyclingConditionsString = cyclingConditionsString.replace(
          /NaN/g,
          "null",
        );

        const cyclingConditions = JSON.parse(cyclingConditionsString);
        return cyclingConditions;
      } catch (error) {
        console.error(error);
      }
    },
    async shuffleToNextSubmission(geo) {
      this.shuffling = true;
      this.shuffleError = false;
      try {
        const response = await axios.post(`${process.env.VUE_APP_SERVER_URL}/shuffle`, { geo: geo, current: this.submissionId });
        const id = response.data && response.data.submissionId;
        if (!id || id === this.submissionId) throw new Error('No other report available');
        // App.vue keys the page by report id, so this opens a fresh page for the new report
        await this.$router.push(this.$lp(`/view/${id}`));
        window.scrollTo(0, 0);
      } catch (error) {
        console.error('Error fetching submission ID:', error);
        this.shuffleError = true;
      } finally {
        this.shuffling = false;
      }
    },
    getRatingClass(rating) {
      if (rating <= 1.0) {
        return "excellent";
      } else if (rating <= 2.0) {
        return "good";
      } else if (rating <= 3.0) {
        return "average";
      } else if (rating <= 4.0) {
        return "poor";
      } else {
        return "bad";
      }
    },
    async getContentRecs() {
      try {
        // const content = `${this.submission.comment}`;
        // get content recs for best practices (advice in array index 1)
        // older reports may have no (or fewer) AI ideas: use whatever text exists
        const adviceArray = Array.isArray(this.submission.advice_array) ? this.submission.advice_array : [];
        const bestPractice = adviceArray[1]?.advice || adviceArray[0]?.advice || "";
        const content = `${this.submission.comment || ""} ${bestPractice}`.trim();
        if (!content) return null;
        const contentArray = Array.isArray(this.submission.content_array) ? this.submission.content_array : [];
        console.log(content);
        const response = await axios.post(
          `${process.env.VUE_APP_SERVER_URL}/get-content-recs`,
          {
            content: JSON.stringify(content),
          },
        );
        const newContentRecs = response.data;

        // Remove duplicates from video_id field
        const uniqueContentRecs = newContentRecs.filter((item) => {
          const existingItem = contentArray.find(
            (existing) => existing.video_id === item.video_id,
          );
          return !existingItem;
        });

        this.contentRecs = uniqueContentRecs;
        return uniqueContentRecs;
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    async fetchImageDetails(id) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_PB_URL}/api/collections/images/records?filter=report='${id}'`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const images = data.items[0].images;
        const imageUrls = images.map(
          (image) =>
            `${process.env.VUE_APP_PB_URL}/api/files/${data.items[0].collectionId}/${data.items[0].id}/${image}`,
        );
        this.submission.imageUrls = imageUrls;
        this.generateAndUploadThumbnail(imageUrls[0], this.submission.id);

        // console.log(imageUrls);
        return imageUrls;
      } catch (e) {
        console.error(e);
      }
    },
    // Thumbnails are a nice-to-have: only create one when the report has none yet,
    // and never let a failure (404 for reports we may not edit, CORS) surface as an error.
    async generateAndUploadThumbnail(imageUrl, recordId) {
      if (!imageUrl || !recordId || this.submission.image) return;
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imageUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        const canvas = document.createElement("canvas");
        canvas.width = 50;
        canvas.height = 50;
        canvas.getContext("2d").drawImage(img, 0, 0, 50, 50);
        const file = this.dataURItoBlob(canvas.toDataURL("image/jpeg", 0.5));
        const formData = new FormData();
        formData.append("image", file, "thumbnail.jpg");
        const record = await this.$pb
          .collection("reports")
          .update(recordId, formData, { requestKey: null });
        this.submission.image = record.image;
      } catch (error) {
        console.warn("Thumbnail not created", error);
      }
    },

    dataURItoBlob(dataURI) {
      const base64 = dataURI.split(",")[1];
      const binaryString = window.atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], { type: "image/jpeg" });
    },

    async voteSubmission(voteType) {
      let updatedCount;
      if (voteType === "upvotes") {
        updatedCount = this.submission.upvotes + 1;
      } else if (voteType === "downvotes") {
        updatedCount = this.submission.downvotes + 1;
      } else {
        throw new Error("Invalid vote type");
      }
      const updatedRecord = await this.$pb
        .collection("reports")
        .update(this.submission.id, {
          upvotes:
            voteType === "upvotes" ? updatedCount : this.submission.upvotes,
          downvotes:
            voteType === "downvotes" ? updatedCount : this.submission.downvotes,
        });
      // print(updatedRecord);
      this.submission.upvotes = updatedRecord.upvotes;
      this.submission.downvotes = updatedRecord.downvotes;
    },

    previousImage() {
      if (this.selectedImageIndex > 0) {
        this.selectedImageIndex--;
      }
    },
    nextImage() {
      if (this.selectedImageIndex < this.submission.imageUrls.length - 1) {
        this.selectedImageIndex++;
      }
    },
    handleKeyDown(event) {
      if (event.key === "Escape") {
        this.selectedImageIndex = null;
      } else if (event.key === "ArrowLeft") {
        this.previousImage();
      } else if (event.key === "ArrowRight") {
        this.nextImage();
      }
    },
    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].clientX;
    },

    // Returns HTML for v-html: every piece of the comment is escaped first, and only
    // http(s) links are turned into anchors, so user text can never inject markup or script.
    addExternalLinkLabels(comment) {
      const escapeHtml = (text) =>
        String(text)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      const text = String(comment ?? "");
      const regex =
        /\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|]/gi;
      let html = "";
      let last = 0;
      for (const match of text.matchAll(regex)) {
        html += escapeHtml(text.slice(last, match.index));
        try {
          const url = new URL(match[0]);
          html += `<a href="${escapeHtml(url.href)}" target="_blank" rel="noopener noreferrer">${this.$t('report.detail.externalLink', { host: escapeHtml(url.hostname) })}</a>`;
        } catch (error) {
          html += escapeHtml(match[0]);
        }
        last = match.index + match[0].length;
      }
      html += escapeHtml(text.slice(last));
      return html;
    },
  },
};
</script>

<style scoped>
/* Report view reads left-aligned (B2): body text, headings and cards all start-aligned. */
.report { text-align: start; display: flex; flex-direction: column; gap: 24px; padding-top: 8px; }
.report .card { text-align: start; }
.report h1, .report h2, .report h3 { text-align: start; }
.report h1, .report h2, .report h3, .report p { margin: 0; }
.report h1 { font-size: 24px; line-height: 1.25; font-weight: 500; }
.report h2 { font-size: 18px; margin-bottom: 8px; }
.muted { color: var(--color-secondary); font-size: 14px; }
.mono { font-family: var(--font-mono); font-size: 14px; }

.report-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.report-bar__id { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; }
.report-bar .btn-round { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; color: inherit; }

.summary { display: flex; flex-direction: column; align-items: stretch; gap: 12px; }
.photos { display: flex; justify-content: flex-start; gap: 8px; margin: 0; padding: 0; list-style: none; max-width: 100%; overflow-x: auto; }
.photo { padding: 0; border: 0; border-radius: var(--radius-lg); overflow: hidden; cursor: pointer; background: var(--color-subtle); flex: none; width: 112px; height: 112px; }
.photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.meta-row { display: flex; align-items: center; justify-content: flex-start; flex-wrap: wrap; gap: 8px; }
.place a { color: var(--color-primary-text); text-decoration: none; }
.place { font-size: 14px; color: var(--color-secondary); }

.status { display: flex; flex-direction: column; gap: 12px; }
.status h2 { margin: 0; }
.status-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }

.story { font-size: 16px; line-height: 1.55; white-space: pre-line; overflow-wrap: anywhere; }
.story.clamped { display: -webkit-box; -webkit-line-clamp: 5; -webkit-box-orient: vertical; overflow: hidden; }
.link-btn { min-height: var(--touch-min); padding: 0 12px; border: 0; background: transparent; color: var(--color-primary-text); font: inherit; font-weight: 600; cursor: pointer; }

.idea { margin: 0; background: var(--color-stickynote); color: var(--color-on-stickynote); border-radius: var(--radius-lg); overflow-wrap: anywhere; }
.idea-label { font-size: 12px; font-weight: 600; margin-bottom: 4px !important; }
.idea a { color: inherit; text-decoration: underline; }
.rate { display: flex; flex-direction: column; gap: 12px; }
.rate h2 { margin-bottom: 0; }

.tonal { background: var(--color-subtle); box-shadow: none; border: 0; display: flex; flex-direction: column; gap: 12px; }
.tonal h2 { margin: 0; }
.support-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.support-actions button { flex: 1 1 140px; }

.hide-ai { align-self: center; }
.ai-hidden { align-items: center; text-align: center; }
.next { display: flex; flex-direction: column; align-items: center; gap: 8px; padding-bottom: 8px; text-align: center; }

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: var(--color-secondary);
  font-size: 20px;
  cursor: pointer;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.image-overlay-content {
  max-width: 90%;
  max-height: 90%;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  position: relative;
}

.image-navigation {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
}

.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.arrow:hover {
  background-color: rgba(255, 255, 255, 0.7);
}

.arrow i {
  color: #000;
}

.arrow.left {
  left: 20px;
}

.arrow.right {
  right: 20px;
}

.image-overlay-content img {
  width: 100%;
  height: auto;
}

button.arrow { border: 0; padding: 0; }
.arrow.close-photo { top: 20px; right: 20px; left: auto; transform: none; }
.happened { display: flex; flex-wrap: wrap; gap: 6px; list-style: none; margin: 0 0 8px; padding: 0; }
.happened li { padding: 4px 10px; border-radius: var(--radius-pill); background: var(--color-subtle); color: var(--color-primary-text); font-size: 12px; font-weight: 500; }
</style>
